import { Component, OnInit, ChangeDetectorRef, Input, Output, EventEmitter, OnDestroy, HostListener, ElementRef, ViewChild } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import L, { Map, latLng, Layer, tileLayer } from "leaflet";
import { AppConfigService } from "src/app/app-config.service";
import { AppConstants } from "src/app/constants/app-constants";
import { MapUtils } from "src/app/utils/map-utils";
import { Alarm } from "src/models/alarm.model";
import { ApiResponse } from "src/models/api-response.model";
import { Branch } from "src/models/branch.model";
import { AlarmService } from "../../services/alarm.service";
import { BranchService } from "../../services/branch.service";
import { AnalyticalDashboardService } from "src/app/services/analytical-dashboard.service";
import { Utility } from "src/app/utils/app-utils";
import { AuthenticationService } from "src/app/services/authentication.service";
import { Subscription } from 'rxjs';
import { MapService } from '../../services/map.service';
import { ConfidenceMonitoringService } from '../../services/confidence-monitoring.service';
import { SocketService } from '../../services/socket.service';
import { WEB_SOCKET_EVENTS } from '../../enums/web-socket-events';

@Component({
  selector: "app-map",
  templateUrl: "./map.component.html",
  styleUrls: ["./map.component.scss"],
})
export class MapComponent implements OnInit, OnDestroy {
  allMarkers = [];
  leafletMap: Map;
  markers: Layer[] = [];
  options: any;
  center = latLng(25.276987, 55.296249);
  zoom = 5;
  branches: Branch[] = [];
  branchSearchResult: Branch[] = [];
  searchText: string;
  APP_CONFIG = this.appConfigService.appConfig;
  @Output() doneLoading = new EventEmitter<boolean>();
  interval: number;
  analyticalDashboardSubscribe: Subscription;
  newAlarmSubscriber: Subscription;
  branchesSubscriber: Subscription;
  operatorDashboardSubscribe: Subscription;
  @Input() shouldShowSearchInput: boolean = false;
  shouldMakeSearchVisible: boolean = true;
  utility = Utility;
  selectedAlarm: Alarm;
  private currentDisplayedBranchId: string = null;
  private resizeObserver: ResizeObserver;
  @ViewChild('mapWrapper', { static: true }) mapWrapper: ElementRef<HTMLDivElement>;

  constructor(
    private branchService: BranchService,
    private alarmService: AlarmService,
    private appConfigService: AppConfigService,
    private translateService: TranslateService,
    public analyticalDashboard: AnalyticalDashboardService,
    private changeDetector: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    public translate: TranslateService, public mapService: MapService,
    public confidenceMonitoringService: ConfidenceMonitoringService,
    private socketService: SocketService
  ) { }

  async ngOnInit() {
    // this.restoreLastMarkerLocation();
    await this.configMapOptions();
    this.branches = await this.getBranches();
    this.analyticalDashboardListener();
    this.operatorDashboardListener();
    this.newAlarmAddedListener();
    await this.selectedAlarmListener();
    this.webSocketInit();
  }

  async ngOnDestroy() {
    this.operatorDashboardSubscribe = Utility.Unsubscribe(this.operatorDashboardSubscribe);
    this.analyticalDashboardSubscribe = Utility.Unsubscribe(this.analyticalDashboardSubscribe);
    this.newAlarmSubscriber = Utility.Unsubscribe(this.newAlarmSubscriber);
    this.branchesSubscriber = Utility.Unsubscribe(this.branchesSubscriber);

    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (this.leafletMap) {
      this.leafletMap.remove();
      this.leafletMap = null;
    }
  }

  async branchesUpdateListener() {
    this.branchesSubscriber = this.branchService.getBranchesChangeListener().subscribe(async (branch: any) => {
      await this.updateMap();
    });
  }

  newAlarmAddedListener() {
    this.newAlarmSubscriber = this.alarmService.getNewAlarmNotification().subscribe((alarm: any) => {
      // Check if the alarm is for the currently displayed branch (either selected or searched)
      if (alarm?.branch?.id === this.currentDisplayedBranchId) {
        this.changeMarkerStatus(alarm);
      }
    });
  }

  changeMarkerStatus(alarm: any) {
    for (const branch of this.branches) {
      if (branch.id === alarm.branch.id) {
        branch.status = alarm?.branch?.status || branch?.status;
        branch.icon = alarm?.branch?.statusIcon || branch?.icon;
        // Create marker for the specific branch
        this.createMarkers({ id: branch.id }, 'branch');
        break;
      }
    }
    this.refreshComponent();
  }

  /**
   * Called to reload the last selected location on map
   */
  restoreLastMarkerLocation() {
    if (localStorage.getItem("prevAlarm")) {
      this.zoomToBranch(JSON.parse(localStorage.getItem("prevAlarm")).branch);
      this.refreshComponent();
    }
  }

  async createMarkers(data?: any, type?: string) {
    if (this.branches && this.branches.length) {
      this.markers = [];
      switch (type) {
        case 'branch':
          let branchDetails = this.branches.find(x => x.id == data.id);
          if (branchDetails) {
            this.markers.push(MapUtils.createMarker(branchDetails, this.translateService));
          }
          break;
        case 'related-branches':
          let relatedBranchesWithIcons = [];
          for (let i = 0; i < data.length; i++) {
            let branchDetails = this.branches.find(x => x.id == data[i].id);
            relatedBranchesWithIcons.push(branchDetails)
          }
          relatedBranchesWithIcons.forEach((branch: Branch) => {
            if (branch) {
              this.markers.push(MapUtils.createMarker(branch, this.translateService));
            }
          });
          break;
        default:
          this.branches.forEach((branch: Branch) => {
            if (branch) {
              this.markers.push(MapUtils.createMarker(branch, this.translateService));
            }
          });
      }
    }
    this.allMarkers = this.markers;
  }

  /**
   * called to config map options
   */
  async configMapOptions() {
    const appConfigInitializer = setInterval(() => {
      this.APP_CONFIG = this.appConfigService.appConfig;
      if (this.APP_CONFIG) {
        // Dynamically set LAYER_NAME (Light or Dark)
        const layerUrl = this.APP_CONFIG.MAP_CONFIG.DARK ?
          'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png' :
          this.APP_CONFIG.MAP_CONFIG.LAYER_NAME;

        const LAYER_OSM = tileLayer(layerUrl, {
          maxZoom: this.APP_CONFIG.MAP_CONFIG.MAX_ZOOM,
          attribution: this.APP_CONFIG.MAP_CONFIG.ATTRIBUTION,
          minZoom: 2,
        });
        // Limit Map Draging - Keeping selected branch location according to map bounds 
        // #378 Staging
        var southWest = L.latLng(180, 180),
          northEast = L.latLng(-180, -180),
          bounds = L.latLngBounds(southWest, northEast);

        this.options = {
          layers: [LAYER_OSM],
          zoom: this.APP_CONFIG.MAP_CONFIG.DEFAULT_ZOOM,
          dragging: true,
          maxBounds: bounds,
          center: latLng(
            this.APP_CONFIG.MAP_CONFIG.DEFAULT_LATITUDE,
            this.APP_CONFIG.MAP_CONFIG.DEFAULT_LONGITUDE
          ),
        };
        // this.initializeMarkers();
        clearInterval(appConfigInitializer);
      }
      this.refreshComponent();
    }, AppConstants.APP_CONFIG_INIT_TIMER);
  }

  // Called when map view toggle after zone view
  async selectedAlarmListener() {
    this.selectedAlarm = this.alarmService.selectedAlarm;
    if (this.selectedAlarm) {
      this.markers = this.allMarkers || [];
      localStorage.setItem("prevAlarm", JSON.stringify(this.selectedAlarm));
      this.currentDisplayedBranchId = this.selectedAlarm.branch.id;
      this.zoomToBranch(this.selectedAlarm.branch);
      this.refreshComponent();
    }
  }

  /**
   * called when alarm is selected on operator dashboard screen
   */
  operatorDashboardListener() {
    this.operatorDashboardSubscribe = this.alarmService.getOnAlarmSelectedListener().subscribe((alarm: Alarm) => {
      if (alarm) {
        this.markers = this.allMarkers || [];
        localStorage.setItem("prevAlarm", JSON.stringify(alarm));
        this.changeMarkerStatus(alarm);
        this.currentDisplayedBranchId = alarm.branch.id;
        this.zoomToBranch(alarm.branch);
        this.refreshComponent();
      }
      else {
        this.defaultMapLocation();
      }
    });
  }

  /**
   * called when a row is selected from alarms, operators or sites table
   */
  analyticalDashboardListener() {
    this.analyticalDashboardSubscribe = this.analyticalDashboard.getOnAlarmSelectedNotification().subscribe(async (alarm: any) => {
      const isOperator = this.authenticationService.hasRole(AppConstants.Operator);
      clearInterval(this.interval);
      if (alarm && !isOperator) {
        this.markers = [];
        switch (this.analyticalDashboard.chartSelected) {
          case this.analyticalDashboard.chartType.ALARM:
            this.zoomToBranch(alarm.branch);
            break;
          case this.analyticalDashboard.chartType.OPERATOR:
            this.zoomToMultipleBranch(alarm.relatedSites);
            break;
          default:
            // when site is selected then call to zoom on branch
            let branch = {
              ...alarm,
              latitude: alarm.location.latitude,
              longitude: alarm.location.longitude
            };
            this.zoomToBranch(branch);
        }
      }
      else if (!alarm) {
        switch (this.analyticalDashboard.chartSelected) {
          case this.analyticalDashboard.chartType.ALARM:
            this.zoomToMultipleBranch(this.branches);
            break;
        }
      }
    });
  }

  /**
   * Initializes markers
   */
  async initializeMarkers() {
    this.markers.forEach((element) => {
      this.allMarkers.push(element);
    });
    this.refreshComponent();
  }

  async updateMap() {
    if (this.markers.length) {
      this.markers.forEach((marker) => {
        if (this.leafletMap) {
          this.leafletMap.removeLayer(marker);
        }
      });
    }
    this.createMarkers();
    this.refreshComponent();
  }

  /**
   * Searches branches
   */
  async searchBranch() {
    this.branchSearchResult = [];
    if (this.searchText === "") {
      this.branchSearchResult = await this.getBranches();
      this.currentDisplayedBranchId = null;
    } else {
      this.branchSearchResult = await this.getBranches(this.searchText);
      this.currentDisplayedBranchId = this.branchSearchResult[0]?.id;
    }
    this.refreshComponent();
  }

  /**
   * Fetches branches from API
   *
   * @param searchText Text to search
   */
  async getBranches(searchText?: string): Promise<Branch[]> {
    try {
      const response: ApiResponse = await this.branchService.getMapBranches(
        searchText
      );
      if (response.success === true) {
        return response.data;
      }
    } catch (err) { }
  }

  /**
   * Pans map to the branch location
   *
   */
  zoomToBranch(branch: Branch) {
    this.center = latLng(branch.latitude, branch.longitude);
    this.zoom = 16;
    this.branchSearchResult = [];
    this.searchText = "";
    if (branch.id) {
      this.createMarkers(branch, 'branch');
    }
    this.refreshComponent();
  }

  async defaultMapLocation() {
    this.branches = await this.getBranches();
    let alarmsList = await this.alarmService.getAlarms();
    if (alarmsList.data.length === 0) {
      if (!this.analyticalDashboard.chartSelected) {
        this.zoomToMultipleBranch(this.branches);
      }
    };
  }

  /**
   * Triggered when map is ready.
   * @param map Leaflet map object
   */
  async onMapReady(map: Map) {
    this.leafletMap = map;
    // calls to done loading after map is loaded
    const interval = setInterval(() => {
      if (this.leafletMap) {
        this.doneLoading.emit(true);
        clearInterval(interval);
      }
    }, AppConstants.MAP_LOADING_CONFIG_TIMER);
    this.refreshComponent();
    // this.shouldMakeSearchVisible = true;

    await this.defaultMapLocation();
    await this.branchesUpdateListener();

    // Resize observer to handle gridstack resizing
    this.resizeObserver = new ResizeObserver(() => {
      if (this.leafletMap) {
        this.leafletMap.invalidateSize();
      }
    });

    const wrapperElement = this.mapWrapper?.nativeElement;
    if (wrapperElement) {
      this.resizeObserver.observe(wrapperElement);
    }
  }

  @HostListener('window:resize')
  onResize() {
    if (this.leafletMap) {
      setTimeout(() => {
        this.leafletMap.invalidateSize();
      }, AppConstants.MAP_LOADING_CONFIG_TIMER);
    }
  }

  /**
   * called to search a site on map
   */
  onSearchExit() {
    this.searchText = "";
    this.branchSearchResult = [];
    this.currentDisplayedBranchId = null;
    this.searchBranch();
    this.createMarkers();
    this.refreshComponent();
  }

  /**
   * called to zoom to multiple sites
   */
  async zoomToMultipleBranch(branches: any) {
    const markerArray = [];
    if (branches && branches.length !== 0) {
      branches.forEach((branch: Branch) => {
        markerArray.push(L.marker([branch.latitude, branch.longitude]));
      });
      const group = L.featureGroup(markerArray);
      if (this.leafletMap) {
        this.leafletMap.fitBounds(group.getBounds());
      }
      this.createMarkers(branches, 'related-branches');
    }
    this.branchSearchResult = [];
    this.searchText = "";
    this.refreshComponent();
  }

  /**
   * We are manually detecting changes to improve performance of page.
   * Please call this function whenever you make changes.
   */
  private refreshComponent() {
    this.changeDetector.markForCheck();
  }

  // WebSocket Listener on Remove Alarm
  webSocketInit() {
    this.socketService.initSocket();
    const socketId = localStorage.getItem('socketId');
    if (!socketId) {
      return;
    }
    const eventNotification = this.socketService.onEvent<any>(socketId);
    if (eventNotification) {
      eventNotification.subscribe((socketEvent) => {
        const data = socketEvent.data;
        switch (socketEvent.type) {
          case WEB_SOCKET_EVENTS.REMOVE_ALARM:
            const alarmData = data.alarm ? data.alarm : data;
            this.onRemoveAlarmEvent(alarmData);
            break;
          case WEB_SOCKET_EVENTS.TAKEN_OVER_ALARM:
            // this.onTakenOverAlarm(data);
            break;
          case WEB_SOCKET_EVENTS.REFRESH_SUPER_ROUTES:
            this.refreshSupervisorRoutes(data);
            break;
          default:
        }
      });
    }
  }

  async onRemoveAlarmEvent(data: any) {
    let alarmsList = await this.alarmService.getAlarms();
    if (alarmsList.data.length === 0 && this.alarmService.getActiveTabIndex() === 0) {
      if (!this.analyticalDashboard.chartSelected) {
        this.defaultMapLocation();
      }
    }
  }

  onTakenOverAlarm(data: any) {
    this.onRemoveAlarmEvent(data.alarm);
  }

  refreshSupervisorRoutes(data: any) {
    if (data === "newAlarm") {
      this.initializeMarkers();
    }
  }

}
